import React, {
  forwardRef,
  ReactChild,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  KeyboardAvoidingView,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native'

import { differenceInMonths } from 'date-fns'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { INTL_PRIVACY_POLICY_LINK } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { AI_COACH_TERMS, AI_COACH_TERMS_INTL } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/messages'
import { CheckboxButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { MESSAGE_AUTHOR_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { Checkbox } from '../../atoms/formElements/Checkbox'
import { AttentionIcon } from '../../atoms/icons/AttentionIcon'
import { AuroraAICoachAvatarIcon } from '../../atoms/icons/AuroraAICoachAvatarIcon'
import { Avatar, avatarAltTextGeneric } from '../../atoms/icons/Avatar'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { Link } from '../../atoms/link/Link'
import { NewMessageIndicator } from '../../atoms/newMessageIndicator/NewMessageIndicator'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { StyledMarkdown } from '../../atoms/styledMarkdown/StyledMarkdown'
import { Title } from '../../atoms/title/Title'
import { TypingIndicator } from '../../atoms/typingIndicator/TypingIndicator'
import { BlockedChatView } from '../../molecules/blockedChatView/BlockedChatView'
import { EmptyMessagesView } from '../../molecules/emptyMessagesView/EmptyMessagesView'
import { EssentialsPressInfo } from '../../molecules/essentialsBubble/EssentialsBubble'
import { OnAiCoachFeedbackPressedType } from '../../molecules/messageItem/MessageItem'
import { PatientHasGuardianBanner } from '../../molecules/patientHasGuardianBanner/PatientHasGuardianBanner'
import { ChatHeader, CloseStyles } from '../../organisms/chatHeader/ChatHeader'
import { ChatInput, ChatInputHandle } from '../../organisms/chatInput/ChatInput'
import { Messages } from '../../organisms/messages/Messages'
import { BodyTextSize, getTypeStyles, TitleSize } from '../../styles/typeStyles'
import { AuroraAwareBackground } from '../../templates/auroraAwareBackground/AuroraAwareBackground'
import { Flex1View, SpacedView } from '../../templates/content/CommonViews'
import { ActivityReviewInfo } from '../../ui-models/assignments/Assignments'
import { Message, MessageAttachmentData } from '../../ui-models/messages/Messages'
import { openUrl } from '../../utils/expo-linking'
import { getDaysSinceFirstMessageFromAuthor, hasProviderMessages } from '../../utils/messageUtils'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const IS_WEB = Platform.OS === 'web'
const SCROLL_THRESHOLD = 50

type InsertMessageOptions = {
  onSend?: () => void
  attributesToAttach?: Record<string, any>
}

export type ChatProps = {
  displayName: string
  banner?: ReactChild | null
  chatHeaderTitle: string
  chatHeaderSubTitleMsg?: ReactNode
  liveSessionBadge: string
  onBack?: () => void
  messages: Message[]
  onSendMessage: (text: string) => void
  onInputChange: (text?: string) => void
  avatarImage?: string
  receiverTyping: boolean
  receiverMsgable: boolean
  receiverBlocked?: boolean
  showChevron?: boolean
  showNewMsgIndicator?: boolean
  scrollInfo?: ChatScrollInfo
  onScrollComplete?: () => void
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
  onNewMsgPressed?: (dismissNewMsgIndicator?: boolean) => void
  unreadMsgCount?: number
  onChatBubblePressed?: (messageId: string, receiver: boolean, activityInfo?: ActivityReviewInfo) => void
  onLinkPress?: (link: string) => void
  closeStyle?: CloseStyles
  chatHeaderCentered?: boolean
  inputValue?: string
  conversationDateCreated?: string
  isLoading?: boolean
  inputActions?: ReactNode[]
  inputAttachments?: ReactNode[]
  inputTopAttachments?: ReactNode[]
  inputSelection?: { start: number; end: number }
  markMessagesAsUnread?: () => void
  showMenu?: boolean
  shouldInvert?: boolean
  showClose?: boolean
  onChevronPressed?: () => void
  onContactCareNavigatorPressed?: () => void
  onAiCoachCareNavigatorPressed?: () => void
  showBlockedChatView?: boolean
  sendOnEnter?: boolean
  onAttachmentPressed?: (attachment: MessageAttachmentData, item: Message) => void
  contactCarePhoneNumber?: string
  careTeamEmail?: string
  showHeaderDivider?: boolean
  hideChatHeader?: boolean
  expandedButton?: boolean
  showEmptyChatView?: boolean
  disableSend?: boolean
  onReachedMessagesEnd?: () => void
  isLoadingNextPage?: boolean
  HeaderTitleBadge?: ReactNode
  onEssentialsPress?: (info: EssentialsPressInfo) => void
  isAiCoachSelected?: boolean
  isProviderAiCoachView?: boolean
  onAiCoachFeedbackPressed?: OnAiCoachFeedbackPressedType
  showAiChatConsent?: boolean
  hasConsentedToAiChat?: boolean
  onConsentToAiChat?: (enable: boolean) => Promise<any>
  isInternational?: boolean
  showProviderUnavailableMessage?: boolean
  hideTimestampOnHover?: boolean
  showFeedbackOnHoverLeft?: boolean
  onVoiceChatPressed?: () => void
  hasGuardian?: boolean
  guardianName?: string
  patientFullName?: string
  isDAEngageProvider?: boolean
  isMinor?: boolean
  dblAttachmentsDisabled?: boolean
}

export type ChatHandle = {
  focusInput: () => void
  insertMessage: (message: string, opts?: InsertMessageOptions) => void
  get isReady(): boolean
}

export interface ChatScrollInfo {
  scrollToEnd: boolean
  animatedScroll: boolean
  scrollForTyping?: boolean
}

const TypingIndicatorContainer = styled(TypingIndicator)<{ receiverTyping: boolean }>(({ theme, receiverTyping }) => ({
  marginLeft: receiverTyping ? theme.spacing['16px'] : 0,
}))

const Container = styled(Flex1View)<{ useAuroraGradient?: boolean }>(({ theme, useAuroraGradient }) => ({
  backgroundColor: useAuroraGradient ? 'transparent' : theme.colors.backgroundPrimary,
  flexShrink: 1,
}))

const ReceiverTypingContainer = styled.View<{ hasAvatar: boolean }>(({ hasAvatar }) => ({
  flexDirection: 'row',
  paddingLeft: hasAvatar ? 0 : 15,
}))

const NewMessageIndicatorContainer = styled.View<{ chatInputHeight: number }>(({ chatInputHeight }) => ({
  bottom: Platform.OS === 'web' ? '10px' : 24 + chatInputHeight,
  alignSelf: 'center',
  justifyContent: 'center',
  position: 'absolute',
}))

const AvatarContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['16px'],
}))

const NoPreviousMessageContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundWarning,
  color: `${theme.colors.textSecondary}`,
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: theme.spacing['16px'],
  gap: theme.spacing['4px'],
}))

const LoadingContainer = styled(Flex1View)(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
}))

const ChatInnerFlexContainer = styled.View({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const ChatMessagesContainer = styled(Flex1View)(({ theme }) => ({
  overflow: 'hidden',
  flexGrow: 1,
  flexShrink: 1,
  ...(IS_WEB && theme.breakpoints.isMobileSized && { height: '75vh' }),
}))

const ProviderUnavailableMessageContainer = styled(Flex1View)(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: theme.spacing['16px'],
  maxHeight: '90px',
  borderTopWidth: '1px',
  borderColor: theme.colors.borderDefault,
}))

const EmptyChatView = styled(Flex1View)(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
}))

const UnavailableTextContainer = styled(Flex1View)(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  flexGrow: 1,
  flexShrink: 1,
  marginLeft: theme.spacing['8px'],
  maxHeight: IS_WEB ? '52px' : '90px',
}))

const LoadingNextPageView = styled(Flex1View)({
  justifyContent: 'center',
  alignItems: 'center',
})
/**
 * This page is the thread conversation between a client and a provider
 * It consists of a header, a list of messages, and an input field for sending messages
 */
export const Chat = forwardRef<ChatInputHandle, ChatProps>(
  (
    {
      displayName,
      banner,
      chatHeaderSubTitleMsg,
      liveSessionBadge,
      onBack = noop,
      messages,
      onSendMessage,
      onInputChange,
      avatarImage,
      receiverTyping,
      receiverMsgable,
      showNewMsgIndicator,
      scrollInfo,
      onScrollComplete,
      onScroll,
      onNewMsgPressed,
      unreadMsgCount,
      onChatBubblePressed,
      onLinkPress,
      chatHeaderTitle,
      chatHeaderCentered,
      HeaderTitleBadge,
      closeStyle,
      showClose = true,
      showChevron = false,
      inputValue,
      conversationDateCreated,
      isLoading,
      inputActions,
      inputAttachments,
      inputTopAttachments,
      inputSelection,
      markMessagesAsUnread,
      showMenu = false,
      shouldInvert = true,
      onChevronPressed,
      receiverBlocked = false,
      onContactCareNavigatorPressed,
      onAiCoachCareNavigatorPressed,
      showBlockedChatView = false,
      sendOnEnter,
      onAttachmentPressed,
      contactCarePhoneNumber,
      careTeamEmail,
      showHeaderDivider,
      hideChatHeader = false,
      expandedButton = false,
      showEmptyChatView = false,
      disableSend = false,
      onReachedMessagesEnd: onReachedChatEnd,
      isLoadingNextPage = false,
      onEssentialsPress,
      isAiCoachSelected,
      isProviderAiCoachView,
      onAiCoachFeedbackPressed,
      showAiChatConsent,
      hasConsentedToAiChat,
      onConsentToAiChat,
      isInternational = false,
      showProviderUnavailableMessage = true,
      hideTimestampOnHover,
      showFeedbackOnHoverLeft,
      onVoiceChatPressed,
      hasGuardian,
      guardianName,
      patientFullName,
      isDAEngageProvider,
      isMinor,
      dblAttachmentsDisabled,
    },
    ref,
  ) => {
    const [chatInputHeight, setChatInputHeight] = useState(60)
    const [isScrolled, setIsScrolled] = useState(false)
    const scrollPosition = useRef(0)
    const readyForNewMessage = useRef(false)
    const [chatMessagesCount, setChatMessagesCount] = useState<number>(messages.length)
    const [markedMessagesAsUnread, setMarkedMessagesAsUnread] = useState(false)
    const chatInputRef = useRef<ChatInputHandle>(null)
    const [hasAgreedToAiChatConsent, setHasAgreedToAiChatConsent] = useState(hasConsentedToAiChat)
    const [consentLoading, setConsentLoading] = useState(false)
    const [termsChecked, setTermsChecked] = useState(false)
    const [dataConsentChecked, setDataConsentChecked] = useState(false)
    const internationalConsentReady = termsChecked && dataConsentChecked
    const theme = useTheme() as ThemeType
    const { colors, isAuroraTheme } = theme
    const typeStyles = getTypeStyles(colors, theme.fontFamilyMetadata)
    const previousMessagesLength = usePrevious(messages.length)
    useImperativeHandle(
      ref,
      () => ({
        focusInput: () => {
          chatInputRef.current?.focusInput()
        },
        insertMessage: (message: string) => {
          chatInputRef.current?.insertMessage(message)
        },
        get isReady() {
          return chatInputRef.current?.isReady ?? false
        },
      }),
      [],
    )
    useEffect(() => {
      if (Platform.OS === 'web') {
        setChatMessagesCount((prevState) => {
          if (messages?.length > prevState) {
            if (markedMessagesAsUnread && messages[0].authorType === 'patient') {
              setMarkedMessagesAsUnread(false)
            }
            return messages.length
          }
          return prevState
        })
      }
    }, [chatMessagesCount, markedMessagesAsUnread, messages, messages.length])

    // Ensure we only fire new msg pressed when we have new messages
    useEffect(() => {
      if (previousMessagesLength < messages.length) {
        readyForNewMessage.current = true
      }
    }, [messages.length, previousMessagesLength])

    useEffect(() => {
      if (
        unreadMsgCount &&
        unreadMsgCount > 0 &&
        scrollPosition.current < SCROLL_THRESHOLD &&
        (markedMessagesAsUnread === false || !markMessagesAsUnread) &&
        readyForNewMessage.current
      ) {
        onNewMsgPressed && onNewMsgPressed(false)
        readyForNewMessage.current = false
      }
    }, [
      onNewMsgPressed,
      unreadMsgCount,
      markedMessagesAsUnread,
      markMessagesAsUnread,
      previousMessagesLength,
      messages.length,
    ])

    // handle scrolling to bottom when receiver is typing and within threshold
    useEffect(() => {
      if (!IS_WEB && receiverTyping && scrollPosition.current < SCROLL_THRESHOLD) {
        onNewMsgPressed && onNewMsgPressed(false)
      }
    }, [receiverTyping, onNewMsgPressed])

    useEffect(() => {
      if (Platform.OS === 'web' && showEmptyChatView && chatInputRef.current) {
        chatInputRef.current.insertMessage('')
      }
    }, [showEmptyChatView])

    const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollYPosition = event?.nativeEvent?.contentOffset?.y
      scrollPosition.current = scrollYPosition
      // Use threshold to prevent false positives from minor layout-induced scroll events on native
      setIsScrolled(scrollYPosition > SCROLL_THRESHOLD)
      onScroll && onScroll(event)
    }

    const send = (text: string) => {
      if (!receiverMsgable) return
      onSendMessage(text)
    }

    const getReceiverView = () => {
      return receiverTyping ? (
        <ReceiverTypingContainer hasAvatar={!!avatarImage}>
          {avatarImage && (
            <AvatarContainer>
              {isAuroraTheme && isAiCoachSelected ? (
                <AuroraAICoachAvatarIcon size={48} />
              ) : (
                <Avatar
                  details={{ src: avatarImage, displayName }}
                  accessibilityLabel={formatMessage(avatarAltTextGeneric, { name: displayName })}
                />
              )}
            </AvatarContainer>
          )}
          <TypingIndicatorContainer receiverTyping={receiverTyping} />
        </ReceiverTypingContainer>
      ) : null
    }

    const getNewMessageView = () => {
      return scrollInfo &&
        showNewMsgIndicator &&
        unreadMsgCount &&
        scrollPosition.current &&
        scrollPosition.current > SCROLL_THRESHOLD ? (
        <NewMessageIndicatorContainer chatInputHeight={chatInputHeight}>
          <NewMessageIndicator onPress={() => onNewMsgPressed?.(true)} plural={unreadMsgCount !== 1} />
        </NewMessageIndicatorContainer>
      ) : null
    }

    const { formatMessage } = useIntl()
    const isConversationThirteenMonthsOld =
      conversationDateCreated && differenceInMonths(new Date(), new Date(conversationDateCreated)) > 13

    const emptyView = useMemo(
      () => (
        <EmptyMessagesView
          avatarDetails={avatarImage ? { src: avatarImage || '', displayName } : undefined}
          displayName={displayName}
          patientFullName={patientFullName}
          guardianName={guardianName}
          avatarOverride={isAuroraTheme && isAiCoachSelected ? <AuroraAICoachAvatarIcon size={80} /> : undefined}
        />
      ),
      [avatarImage, displayName, patientFullName, guardianName, isAuroraTheme, isAiCoachSelected],
    )
    if (isLoading) {
      return (
        <LoadingContainer>
          <LoadingIndicator size={45} />
        </LoadingContainer>
      )
    }

    const canSendMessages = !receiverBlocked && receiverMsgable
    const handleMarkMessagesAsUnread = () => {
      setMarkedMessagesAsUnread(true)
      markMessagesAsUnread && markMessagesAsUnread()
    }
    const hasAvatar = !!avatarImage

    const shouldShowAiChatConsent = showAiChatConsent && isAiCoachSelected && !hasAgreedToAiChatConsent
    const dividerColor = isAuroraTheme ? colors.borderDefault : undefined
    const consentView = (
      <>
        <ScrollView testID={tID('Chat-AiConsentView')} style={{ padding: 16 }}>
          <View style={{ marginBottom: 8 }}>
            <Title
              text={
                <FormattedMessage
                  defaultMessage='Accept terms to continue.'
                  description='Title for AI chat user agreement'
                />
              }
              size={TitleSize.MEDIUM}
            />
            <SpacedView marginTop={'16px'} marginBottom={'16px'}>
              <BodyText
                text={
                  <FormattedMessage
                    defaultMessage='Please agree to our terms and conditions in order to use Lyra AI:'
                    description='Prompt asking user to consent to AI chat terms'
                  />
                }
              />
            </SpacedView>
            <Divider height={1} color={dividerColor} />
            <SpacedView marginTop={'16px'} />
            <StyledMarkdown
              content={isInternational ? AI_COACH_TERMS_INTL : AI_COACH_TERMS}
              customStyles={{ h1: typeStyles.bodyLarge, h2: typeStyles.bodyLarge }}
            />
            {isInternational && (
              <View style={styles.internationalConsentContainer}>
                <Checkbox
                  testID={tID('Chat-AiConsentTermsCheckbox')}
                  name='termsConsent'
                  checked={termsChecked}
                  onPress={() => setTermsChecked(!termsChecked)}
                  buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
                  titleColor={colors.textPrimary}
                  baseInputStyle={{ marginBottom: 16 }}
                  title={
                    <FormattedMessage
                      defaultMessage='I have read and agree to the Lyra AI Terms.{br}<secondary>I understand that Lyra AI is an optional informational and supportive tool, is not a human or qualified mental health professional, and should not be used for emergencies, crisis support, medical advice, diagnosis, treatment, or as a substitute for care from a Lyra Coach, clinician, therapist, counsellor, or other qualified mental health professional.</secondary>'
                      description='Checkbox label for agreeing to Lyra AI terms for international users'
                      values={{
                        br: '\n',
                        secondary: (text: string) => <BodyText color={colors.textSecondary}>{text}</BodyText>,
                      }}
                    />
                  }
                />
                <Checkbox
                  testID={tID('Chat-AiConsentDataCheckbox')}
                  name='dataConsent'
                  checked={dataConsentChecked}
                  onPress={() => setDataConsentChecked(!dataConsentChecked)}
                  buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
                  titleColor={colors.textPrimary}
                  baseInputStyle={{ marginBottom: 0 }}
                  title={
                    <FormattedMessage
                      defaultMessage='I explicitly consent to Lyra collecting and using my personal information, including health and wellbeing information, to provide Lyra AI to me.{br}<secondary>This includes information I provide when using Lyra AI, information generated through my Lyra AI conversations, and relevant information from my Lyra Care Coaching experience, including health and wellbeing information, as described in the Lyra AI Terms and the <link>Lyra Privacy Policy</link>.</secondary>'
                      description='Checkbox label for data consent for Lyra AI for international users'
                      values={{
                        br: '\n',
                        secondary: (text: string) => <BodyText color={colors.textSecondary}>{text}</BodyText>,
                        link: (text: string) => (
                          <Link
                            text={text}
                            onPress={() => openUrl(INTL_PRIVACY_POLICY_LINK)}
                            testID={tID('Chat-AiConsentPrivacyPolicyLink')}
                            underline
                          />
                        ),
                      }}
                    />
                  }
                />
              </View>
            )}
          </View>
        </ScrollView>
        <Divider height={1} color={dividerColor} />
        <View
          style={{
            alignSelf: theme.breakpoints.isMobileSized ? 'center' : 'flex-end',
            width: theme.breakpoints.isMobileSized ? '100%' : 'auto',
            padding: 16,
          }}
        >
          <PrimaryButton
            testID={tID('Chat-AiConsentButton')}
            fullWidth={theme.breakpoints.isMobileSized}
            text={<FormattedMessage defaultMessage='Agree and continue' description='AI consent button text' />}
            loading={consentLoading}
            disabled={isInternational && !internationalConsentReady}
            onPress={async () => {
              setConsentLoading(true)
              if (onConsentToAiChat) {
                const { error } = await onConsentToAiChat(true)
                if (error === undefined) {
                  setHasAgreedToAiChatConsent(true)
                }
              }
              setConsentLoading(false)
            }}
          />
        </View>
      </>
    )

    const shouldShowGuardianBanner =
      hasGuardian &&
      isDAEngageProvider &&
      isMinor &&
      (!hasProviderMessages(messages) ||
        (getDaysSinceFirstMessageFromAuthor(messages, MESSAGE_AUTHOR_TYPE.PROVIDER) ?? Infinity) < 2)

    const patientHasGuardianBanner = (
      <PatientHasGuardianBanner patientName={patientFullName ?? displayName} guardianName={guardianName ?? ''} />
    )

    // Use Aurora gradient background when Aurora theme is active (mobile only)
    const useAuroraGradient = isAuroraTheme && !IS_WEB
    // Hide the header divider for Aurora unless explicitly set
    const shouldShowHeaderDivider = showHeaderDivider ?? !useAuroraGradient

    const chatContent = (
      <Container testID={tID('Chat')} useAuroraGradient={useAuroraGradient}>
        <KeyboardAvoidingView
          behavior={Platform.select({ android: 'height', ios: 'padding' })}
          style={styles.keyboardAvoidingView}
        >
          <ChatInnerFlexContainer testID={tID('Chat-InnerFlexContainer')}>
            {!hideChatHeader && (
              <ChatHeader
                titleMsg={chatHeaderTitle}
                subTitleMsg={chatHeaderSubTitleMsg}
                textBadgeMsg={liveSessionBadge}
                onClosePress={onBack}
                closeStyle={closeStyle}
                titleCentered={chatHeaderCentered}
                showChevron={showChevron}
                showMenu={messages.length > 0 && showMenu}
                markMessagesAsUnread={handleMarkMessagesAsUnread}
                showClose={showClose}
                onChevronPressed={onChevronPressed}
                showDivider={shouldShowHeaderDivider}
                TitleBadge={HeaderTitleBadge}
                isScrolled={isScrolled}
              />
            )}
            {shouldShowAiChatConsent ? (
              consentView
            ) : (
              <>
                {banner}
                {shouldShowGuardianBanner && patientHasGuardianBanner}
                {isConversationThirteenMonthsOld && (
                  <NoPreviousMessageContainer testID={tID('Chat-noPreviousMsgs')}>
                    <AttentionIcon size={24} />
                    <BodyText
                      color={colors.textWarning}
                      textAlign='center'
                      text={
                        <FormattedMessage
                          defaultMessage='Previous messages are not available. Messages are available for 13 months.'
                          description='A notice that tells the user older messages are not displayed'
                        />
                      }
                    />
                  </NoPreviousMessageContainer>
                )}
                {showEmptyChatView ? (
                  <EmptyChatView />
                ) : !canSendMessages && showBlockedChatView ? (
                  <BlockedChatView
                    receiverBlocked={receiverBlocked}
                    onContactCareNavigatorPressed={onContactCareNavigatorPressed}
                    contactCarePhoneNumber={contactCarePhoneNumber}
                    careTeamEmail={careTeamEmail}
                  />
                ) : (
                  <ChatMessagesContainer testID={tID('Chat-messages-container')}>
                    <Messages
                      messages={messages}
                      onScroll={handleScroll}
                      scrollInfo={scrollInfo}
                      onScrollComplete={onScrollComplete}
                      listHeaderComponent={getReceiverView()}
                      hasAvatar={hasAvatar}
                      onChatBubblePressed={onChatBubblePressed}
                      onLinkPress={onLinkPress}
                      NewMessageView={getNewMessageView()}
                      emptyMessagesView={emptyView}
                      shouldInvert={shouldInvert}
                      onAttachmentPressed={(attachment, item) =>
                        onAttachmentPressed && onAttachmentPressed(attachment, item)
                      }
                      onReachedEnd={onReachedChatEnd}
                      listFooterComponent={
                        isLoadingNextPage ? (
                          <LoadingNextPageView>
                            <LoadingIndicator size={24} topPadding={12} />
                          </LoadingNextPageView>
                        ) : null
                      }
                      onEssentialsPress={onEssentialsPress}
                      onAiCoachFeedbackPressed={onAiCoachFeedbackPressed}
                      isAiCoachSelected={isAiCoachSelected}
                      isProviderAiCoachView={isProviderAiCoachView}
                      hideTimestampOnHover={hideTimestampOnHover}
                      showFeedbackOnHoverLeft={showFeedbackOnHoverLeft}
                      dblAttachmentsDisabled={dblAttachmentsDisabled}
                    />
                  </ChatMessagesContainer>
                )}
                <Divider color={dividerColor} />
                {canSendMessages && (
                  <ChatInput
                    ref={chatInputRef}
                    onSendMessage={send}
                    onInputChange={onInputChange}
                    onInputHeightChange={setChatInputHeight}
                    previousInputValue={inputValue}
                    actions={inputActions}
                    sendOnEnter={sendOnEnter}
                    attachments={inputAttachments}
                    topAttachments={inputTopAttachments}
                    expandedButton={expandedButton}
                    disableSend={disableSend}
                    selection={inputSelection}
                    onVoiceChatPressed={onVoiceChatPressed}
                    isAiCoachSelected={isAiCoachSelected}
                  />
                )}
                {!canSendMessages && showProviderUnavailableMessage && (
                  <ProviderUnavailableMessageContainer testID={tID('Chat-ProviderUnavailableMessageContainer')}>
                    <UnavailableTextContainer>
                      <BodyText
                        color={colors.textSecondary}
                        size={IS_WEB ? BodyTextSize.SMALL : BodyTextSize.DEFAULT}
                        text={
                          isAiCoachSelected ? (
                            <FormattedMessage
                              defaultMessage='Lyra AI is no longer active. For support, please reach out to a <link>Care Navigator</link>.'
                              description='Message when AI coach is inactive'
                              values={{
                                link: (text: string) => (
                                  <Link
                                    size={IS_WEB ? BodyTextSize.SMALL : BodyTextSize.DEFAULT}
                                    containerStyle={{ display: 'inline' }}
                                    onPress={(e: any) => {
                                      e.preventDefault()
                                      onAiCoachCareNavigatorPressed?.()
                                    }}
                                    text={text}
                                  />
                                ),
                              }}
                            />
                          ) : (
                            <FormattedMessage
                              defaultMessage='For assistance please contact a <link>Lyra Care Navigator</link>.'
                              description='Message when provider is blocked informing user to contact care navigator'
                              values={{
                                link: (text: string) => (
                                  <Link
                                    size={IS_WEB ? BodyTextSize.SMALL : BodyTextSize.DEFAULT}
                                    containerStyle={{ display: 'inline' }}
                                    onPress={(e: any) => {
                                      e.preventDefault()
                                      onContactCareNavigatorPressed?.()
                                    }}
                                    text={text}
                                  />
                                ),
                              }}
                            />
                          )
                        }
                      />
                    </UnavailableTextContainer>
                  </ProviderUnavailableMessageContainer>
                )}
              </>
            )}
          </ChatInnerFlexContainer>
        </KeyboardAvoidingView>
      </Container>
    )

    return useAuroraGradient ? <AuroraAwareBackground>{chatContent}</AuroraAwareBackground> : chatContent
  },
)

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  internationalConsentContainer: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },
})
